/**
 * RewardReadyPopup — game-style celebratory popup that fires whenever a
 * new claimable reward enters the queue. Replaces silent point credits
 * with a satisfying "tap to claim" moment — see plan Phase 1.2.
 *
 * Triggers:
 *  - on a `reward-queued` window event (fired by useMilestoneAwarder
 *    and the new claimable-points paths in usePoints/useCommonGoalPoints)
 *  - on a pendingRewards count increase observed by react-query
 *
 * Debounces bursts (multiple rewards queued in the same action) into a
 * single popup. Dismissed popups don't reappear until a NEW reward arrives.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePendingRewards } from "@/hooks/usePendingRewards";
import { useAuth } from "@/hooks/useAuth";
import { ClaimDrawer } from "@/components/gamification/ClaimDrawer";
import { acquirePopup, releasePopup, cancelPopup, POPUP_PRIORITIES } from "@/lib/popupQueue";
import { cn } from "@/lib/utils";

const POPUP_ID = "reward-ready";

interface QueuedDetail {
  action?: string;
  points?: number;
  id?: string;
  description?: string;
}

export function RewardReadyPopup() {
  const { user } = useAuth();
  const { count, rewards } = usePendingRewards();
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [latest, setLatest] = useState<{ points: number; description: string } | null>(null);

  const prevCountRef = useRef<number>(count);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSeenIdRef = useRef<string | null>(null);

  const isSuppressed = () => {
    try {
      const until = (window as any).__suppressRewardPopupUntil as number | undefined;
      return typeof until === "number" && Date.now() < until;
    } catch { return false; }
  };

  const requestOpen = (next: { points: number; description: string }) => {
    if (isSuppressed()) return;
    acquirePopup(POPUP_ID, POPUP_PRIORITIES.reward).then(() => {
      if (isSuppressed()) {
        releasePopup(POPUP_ID);
        return;
      }
      setLatest(next);
      setOpen(true);
    });
  };

  // Listen for explicit queue events for instant feedback
  useEffect(() => {
    if (!user?.id) return;
    const handler = (e: Event) => {
      if (isSuppressed()) return;
      const detail = (e as CustomEvent<QueuedDetail>).detail ?? {};
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        requestOpen({
          points: detail.points ?? 0,
          description: detail.description ?? "New reward ready",
        });
      }, 700);
    };
    window.addEventListener("reward-queued", handler);
    return () => {
      window.removeEventListener("reward-queued", handler);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cancelPopup(POPUP_ID);
    };
  }, [user?.id]);

  // Watch query-derived count as a safety net (covers server-side triggers
  // like the community-participation certificate that don't fire the event).
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    if (count > prev && rewards.length > 0) {
      const newest = rewards[0];
      if (newest.id === lastSeenIdRef.current) return;
      lastSeenIdRef.current = newest.id;
      if (!open) {
        requestOpen({
          points: newest.points,
          description: newest.description ?? "New reward ready",
        });
      }
    }
  }, [count, rewards, open]);

  // Release the slot whenever the popup closes
  useEffect(() => {
    if (!open) releasePopup(POPUP_ID);
  }, [open]);

  if (!user?.id) return null;

  const handleClaim = () => {
    setOpen(false);
    setDrawerOpen(true);
  };

  return (
    <>
      <AnimatePresence>
        {open && latest && (
          <motion.div
            key="reward-popup-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Reward ready"
          >
            <motion.div
              initial={{ scale: 0.7, opacity: 0, y: 24 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 16 }}
              transition={{ type: "spring", stiffness: 320, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "relative w-full max-w-sm rounded-3xl overflow-hidden",
                "bg-gradient-to-br from-amber-300 via-orange-400 to-rose-400",
                "shadow-[0_20px_60px_-15px_rgba(245,158,11,0.55)]",
                "border border-amber-200"
              )}
            >
              <button
                type="button"
                aria-label="Close"
                className="absolute top-3 right-3 z-10 h-7 w-7 rounded-full bg-black/15 hover:bg-black/25 text-white flex items-center justify-center"
                onClick={() => setOpen(false)}
              >
                <X className="h-4 w-4" />
              </button>

              {/* Sparkle accents */}
              <motion.div
                aria-hidden
                className="absolute top-6 left-6 text-white/70"
                animate={{ rotate: [0, 20, -10, 0], scale: [1, 1.15, 1] }}
                transition={{ duration: 2.4, repeat: Infinity }}
              >
                <Sparkles className="h-5 w-5" />
              </motion.div>
              <motion.div
                aria-hidden
                className="absolute bottom-10 right-8 text-white/60"
                animate={{ rotate: [0, -25, 10, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.4 }}
              >
                <Sparkles className="h-4 w-4" />
              </motion.div>

              <div className="px-6 pt-8 pb-6 text-center text-white">
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.1 }}
                  className="mx-auto mb-3 h-20 w-20 rounded-full bg-white/95 text-amber-500 shadow-lg flex items-center justify-center"
                >
                  <Gift className="h-10 w-10" strokeWidth={2.4} />
                </motion.div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/85">
                  Reward ready
                </div>
                <div className="mt-1 text-3xl font-extrabold drop-shadow-sm">
                  +{latest.points} pts
                </div>
                <p className="mt-2 text-sm text-white/95 leading-snug px-2 line-clamp-2">
                  {latest.description}
                </p>
                {count > 1 && (
                  <p className="mt-1 text-[11px] text-white/85">
                    You have {count} rewards waiting
                  </p>
                )}

                <div className="mt-5 flex flex-col gap-2">
                  <Button
                    type="button"
                    onClick={handleClaim}
                    className="w-full bg-white text-amber-600 hover:bg-white/95 font-bold shadow-md h-11"
                  >
                    Claim now
                  </Button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="text-xs font-medium text-white/85 hover:text-white underline-offset-2 hover:underline"
                  >
                    Later
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ClaimDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
}
